import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Zap, TrendingUp, Map, ArrowRight, ShieldCheck, BarChart3, FileText } from 'lucide-react';
import { Logo } from '../components/Logo';
export function LandingPage() {
  const navigate = useNavigate();
  const features = [{
    title: 'EPT-2 Forecasting',
    description: 'Our proprietary AI weather model delivers 94% accuracy, outperforming traditional GFS and EC-IFS models.',
    icon: Zap,
    color: 'text-amber-500',
    bg: 'bg-amber-50'
  }, {
    title: 'Real-Time Market Data',
    description: 'Live ENTSO-E electricity prices and generation mix data across major European markets.',
    icon: TrendingUp,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50'
  }, {
    title: 'Regional Intelligence',
    description: 'Comprehensive coverage for Germany, Spain, UK, and France with granular zonal data.',
    icon: Map,
    color: 'text-blue-500',
    bg: 'bg-blue-50'
  }];
  return <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-cyan-50 text-slate-800 font-sans selection:bg-[#ff006b]/20 relative overflow-hidden" data-id="element-3619">
      {/* Animated gradient orbs */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-pink-300/30 via-purple-300/30 to-transparent rounded-full blur-3xl animate-pulse" data-id="element-3620" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-cyan-300/20 via-purple-300/20 to-transparent rounded-full blur-3xl animate-pulse delay-1000" data-id="element-3621" />

      {/* Navigation */}
      <nav className="relative z-10 px-6 py-6 max-w-7xl mx-auto flex justify-between items-center" data-id="element-3622">
        <Logo size="lg" data-id="element-3623" />
        <div className="flex items-center gap-4" data-id="element-3624">
          <button onClick={() => navigate('/ux')} className="px-4 py-2 rounded-lg bg-white/40 hover:bg-white/60 backdrop-blur-sm border border-white/50 text-sm font-medium text-slate-700 transition-all hover:shadow-sm flex items-center gap-2" data-id="element-3625">
            <FileText size={16} data-id="element-3626" />
            <span className="hidden sm:inline" data-id="element-3627">Documentation</span>
          </button>
          <button onClick={() => navigate('/login')} className="px-5 py-2 rounded-lg bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-white/50 text-sm font-medium text-slate-700 transition-all hover:shadow-md" data-id="element-3628">
            Sign In
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-32" data-id="element-3629">
        <div className="text-center max-w-4xl mx-auto mb-24" data-id="element-3630">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-3631">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/60 backdrop-blur-sm border border-pink-100 text-xs font-medium text-pink-600 mb-6 shadow-sm" data-id="element-3632">
              <span className="relative flex h-2 w-2" data-id="element-3633">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75" data-id="element-3634"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-500" data-id="element-3635"></span>
              </span>
              Now live: EPT-2 Model v3.0
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-8 tracking-tight leading-tight" data-id="element-3636">
              Real-Time Energy <br data-id="element-3637" />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#ff006b] to-[#a855f7]" data-id="element-3638">
                Market Intelligence
              </span>
            </h1>
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-3639">
              AI-powered forecasting for European electricity markets. Make
              data-driven decisions with EPT-2, our proprietary weather model.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-3640">
              <button onClick={() => navigate('/login')} className="px-8 py-4 rounded-xl bg-gradient-to-r from-[#ff006b] to-[#a855f7] text-white font-semibold shadow-lg shadow-pink-500/30 hover:shadow-pink-500/40 hover:scale-[1.02] transition-all flex items-center gap-2" data-id="element-3641">
                Live Demo
                <ArrowRight size={18} data-id="element-3642" />
              </button>
              <button onClick={() => navigate('/ux')} className="px-8 py-4 rounded-xl bg-white/60 hover:bg-white/80 backdrop-blur-sm border border-white/50 text-slate-700 font-semibold transition-all hover:shadow-md" data-id="element-3643">
                View Documentation
              </button>
            </div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-32" data-id="element-3644">
          {features.map((feature, idx) => <motion.div key={feature.title} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2 + idx * 0.1
        }} className="bg-white/60 backdrop-blur-xl border border-white/50 rounded-2xl p-8 shadow-xl shadow-slate-200/40 hover:shadow-2xl hover:shadow-pink-500/10 transition-all group" data-id="element-3645">
              <div className={`w-12 h-12 rounded-xl ${feature.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`} data-id="element-3646">
                <feature.icon className={`w-6 h-6 ${feature.color}`} data-id="element-3647" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-3648">
                {feature.title}
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-3649">
                {feature.description}
              </p>
            </motion.div>)}
        </div>

        {/* Social Proof */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-32" data-id="element-3650">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-8" data-id="element-3651">
            Trusted by leading energy companies across Europe
          </p>
          <div className="flex flex-wrap justify-center items-center gap-12 opacity-60 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-3652">
            {/* Mock Logos */}
            <div className="flex items-center gap-2 text-xl font-bold text-slate-800" data-id="element-3653">
              <Zap className="text-blue-600" data-id="element-3654" /> EnergyOne
            </div>
            <div className="flex items-center gap-2 text-xl font-bold text-slate-800" data-id="element-3655">
              <TrendingUp className="text-green-600" data-id="element-3656" /> PowerGrid
            </div>
            <div className="flex items-center gap-2 text-xl font-bold text-slate-800" data-id="element-3657">
              <BarChart3 className="text-purple-600" data-id="element-3658" /> DataFlux
            </div>
            <div className="flex items-center gap-2 text-xl font-bold text-slate-800" data-id="element-3659">
              <ShieldCheck className="text-slate-600" data-id="element-3660" /> SecureEnergy
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-12 text-center relative overflow-hidden shadow-2xl shadow-slate-900/20" data-id="element-3661">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-pink-500/20 rounded-full blur-3xl" data-id="element-3662" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-purple-500/20 rounded-full blur-3xl" data-id="element-3663" />

          <div className="relative z-10" data-id="element-3664">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-3665">
              Ready to transform your trading strategy?
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto text-lg" data-id="element-3666">
              Join the leading energy traders using Jua.ai for superior
              forecasting accuracy.
            </p>
            <button onClick={() => navigate('/login')} className="px-8 py-4 rounded-xl bg-white text-slate-900 font-bold hover:bg-pink-50 transition-colors shadow-lg" data-id="element-3667">
              Live Demo
            </button>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-200/60 bg-white/30 backdrop-blur-md mt-20" data-id="element-3668">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-3669">
          <Logo size="sm" data-id="element-3670" />
          <p className="text-sm text-slate-500" data-id="element-3671">
            © 2024 Jua.ai. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-slate-600" data-id="element-3672">
            <button onClick={() => navigate('/ux')} className="hover:text-[#ff006b] transition-colors" data-id="element-3673">
              Documentation
            </button>
            <a href="#" className="hover:text-[#ff006b] transition-colors" data-id="element-3674">
              Privacy
            </a>
            <a href="#" className="hover:text-[#ff006b] transition-colors" data-id="element-3675">
              Terms
            </a>
            <a href="#" className="hover:text-[#ff006b] transition-colors" data-id="element-3676">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>;
}